import React from 'react';
import { ValidationRule } from '../../types/ValidationRule';
import { getKPIName } from '../../utils/kpiMapping';

interface ValidationRuleTableProps {
  rules: ValidationRule[];
  loading: boolean;
  onEdit: (rule: ValidationRule) => void;
  onDelete: (id: number) => void;
}

const ValidationRuleTable: React.FC<ValidationRuleTableProps> = ({
  rules,
  loading,
  onEdit,
  onDelete
}) => {
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading validation rules...</p>
      </div>
    );
  }

  if (rules.length === 0) {
    return (
      <div className="empty-state">
        <p>No validation rules found. Create your first validation rule by clicking the "Add New Validation Rule" button.</p>
      </div>
    );
  }

  return (
    <div className="table-container">
      <table className="data-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Brand</th>
            <th>Validation Rule</th>
            <th>KPIs</th>
            <th>Weight</th>
            <th>Created At</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rules.map((rule) => (
            <tr key={rule.id}>
              <td>{rule.id}</td>
              <td>
                <span style={{
                  padding: '4px 8px',
                  background: 'rgba(0, 200, 255, 0.2)',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  {rule.brand || 'N/A'}
                </span>
              </td>
              <td>{rule.ruleText}</td>
              <td>
                {(() => {
                  if (!rule.kpiCodes) {
                    return <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}>No KPIs</span>;
                  }
                  
                  try {
                    const kpiCodes = JSON.parse(rule.kpiCodes) as string[];
                    
                    if (!kpiCodes || kpiCodes.length === 0) {
                      return <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}>No KPIs</span>;
                    }
                    
                    return (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {kpiCodes.map((kpiCode, index) => {
                          const kpiName = getKPIName(kpiCode);
                          return (
                            <span
                              key={index}
                              style={{
                                padding: '4px 8px',
                                background: 'rgba(100, 255, 100, 0.15)',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                color: 'rgba(100, 255, 100, 0.9)',
                                border: '1px solid rgba(100, 255, 100, 0.3)'
                              }}
                              title={`KPI Code: ${kpiCode}`}
                            >
                              {kpiName}
                            </span>
                          );
                        })}
                      </div>
                    );
                  } catch (error) {
                    return <span style={{ color: 'rgba(255, 100, 100, 0.8)', fontSize: '11px' }}>Invalid KPI data</span>;
                  }
                })()}
              </td>
              <td>
                <span style={{
                  padding: '4px 8px',
                  background: 'rgba(0, 200, 255, 0.15)',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: 'rgba(0, 200, 255, 0.9)'
                }}>
                  {rule.weight || 0}
                </span>
              </td>
              <td>
                {rule.createdAt 
                  ? new Date(rule.createdAt).toLocaleDateString() 
                  : 'N/A'
                }
              </td>
              <td>
                <div className="action-buttons">
                  <button
                    className="edit-button"
                    onClick={() => onEdit(rule)}
                    title="Edit Validation Rule"
                  >
                    ✏️
                  </button>
                  <button
                    className="delete-button"
                    onClick={() => onDelete(rule.id as number)}
                    title="Delete Validation Rule"
                  >
                    🗑️
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ValidationRuleTable; 